import React, { EventHandler, useCallback, useEffect } from 'react';
import {
  AssemblyTypeKey,
  RpCheck2Type,
  TestRepair,
  TestResult,
  TestResultDc,
  TestResultRp,
  TestResultSvbPvb,
  TestResultType2Bypass,
} from '@ecosconnect/core/src/types/jobFormData';
import Accordion from '../../Accordion/Accordion';
import AccordionItem from '../../Accordion/AccordionItem';
import DcTestResultEdit from './types/DcTestResultEdit';
import { Input } from '../../Form';
import BackflowTestResultStatus from './BackflowTestResultStatus';
import backflowTestResultTitle from './backflowTestResultTitle';
import { maybeNumber } from '@ecosconnect/core/src/helpers/number';
import {
  add,
  format,
  isBefore,
  isValid,
  parse,
  parseISO,
  setYear,
  startOfToday,
  sub,
  subYears,
} from 'date-fns';
import SvbPvbTestResultEdit from './types/SvbPvbTestResultEdit';
import RpTestResultEdit from './types/RpTestResultEdit';
import Type2BypassTestResultEdit from './types/Type2BypassTestResultEdit';
import { Textarea } from '@rebass/forms';
import Button from '../../Button/Button';
import { Text } from 'rebass';

type BackflowTestResultEditProps<T = TestResult | TestRepair> = {
  test: T;
  onChange: (test: T, index: number) => void;
  assemblyType: AssemblyTypeKey;
  rpCheck2Type: RpCheck2Type;
  index: number;
  removeTest?: (index: number) => void;
};

const tenYearsAgo = subYears(new Date(), 20);

const BackflowTestResultEdit: React.FC<BackflowTestResultEditProps> = ({
  test,
  onChange,
  assemblyType,
  rpCheck2Type,
  index,
  removeTest,
}) => {
  const onFormInputChange = useCallback<EventHandler<any>>(
    (e) => {
      if (!e.target.name) {
        throw new Error(`${e.target.tagName} element does not have a name`);
      }
      let value = maybeNumber(e.target.value);
      if (e.target.name === 'testDate' && typeof value === 'string') {
        let isoDate = parseISO(value);
        if (isValid(isoDate)) {
          if (isBefore(isoDate, tenYearsAgo)) {
            // protect against really old dates
            isoDate = setYear(isoDate, new Date().getFullYear());
          }
          value = format(isoDate, 'MM/dd/yyyy');
        }
      }
      onChange(
        {
          ...test,
          [e.target.name]: value,
        },
        index
      );
    },
    [test, index, onChange, assemblyType]
  );

  useEffect(() => {
    if (
      assemblyType.startsWith('RP') &&
      rpCheck2Type !== 'USC10' &&
      (test as TestResultRp).check2 !== 'other'
    ) {
      onChange(
        {
          ...test,
          check2: 'other',
        },
        index
      );
    }
    if (
      (!assemblyType.startsWith('RP') || rpCheck2Type === 'USC10') &&
      (test as any).check2 === 'other'
    ) {
      onChange(
        {
          ...test,
          check2: undefined,
        },
        index
      );
    }
  }, [assemblyType, (test as TestResultRp).check2, test, index, rpCheck2Type]);

  const parsedDate = test.testDate
    ? parse(test.testDate, 'MM/dd/yyyy', startOfToday())
    : undefined;

  const testDate =
    parsedDate && isValid(parsedDate) ? format(parsedDate, 'yyyy-MM-dd') : '';

  const dateInput = (
    <AccordionItem
      label="Date"
      alignItems="center"
      as="label"
      value={
        <Input
          required
          onChange={onFormInputChange}
          variant="accordion"
          type="date"
          name="testDate"
          min={format(
            sub(Date.now(), {
              months: 18,
            }),
            'yyyy-MM-dd'
          )}
          max={format(
            add(Date.now(), {
              days: 1,
            }),
            'yyyy-MM-dd'
          )}
          value={testDate}
          width="150px"
          autoComplete="off"
        />
      }
    />
  );

  // const errors = validateTestResult(test, AssemblyType[assemblyType])

  const removeButton = (
    <Button
      ml={2}
      variant="secondary"
      icon="trash"
      onClick={() => removeTest?.(index)}
    />
  );

  if (test.type === 'repair') {
    return (
      <Accordion key={index} title="Repair" icon={removeButton}>
        {dateInput}
        <AccordionItem
          label="Comments"
          as="label"
          htmlFor="repair-comments"
          value=" "
        />
        <Textarea
          id="repair-comments"
          required
          onChange={onFormInputChange}
          name="comment"
          value={test.comment}
          height={54}
        />
        {test.comment && test.comment.length > 50 && (
          <Text textAlign="right" variant="subheading" fontWeight="normal">
            {test.comment.length} of 255
          </Text>
        )}
        {/*{errors.map((error, index) => (*/}
        {/*  <Text variant="validation" key={index}>*/}
        {/*    {error}*/}
        {/*  </Text>*/}
        {/*))}*/}
      </Accordion>
    );
  }

  return (
    <Accordion
      title={backflowTestResultTitle({
        assemblyType,
        index,
      })}
      icon={
        <>
          <BackflowTestResultStatus
            test={test}
            assemblyType={assemblyType}
            rpCheck2Type={rpCheck2Type}
          />
          {index > 0 ? removeButton : undefined}
        </>
      }
    >
      {dateInput}
      <AccordionItem
        label="System PSI"
        alignItems="center"
        as="label"
        value={
          <Input
            required
            onChange={onFormInputChange}
            variant="accordion"
            type="number"
            name="systemPsi"
            value={test.systemPsi}
            step="1"
            min="0"
            max="500"
            autoComplete="off"
          />
        }
      />
      {assemblyType.startsWith('RP') && (
        <RpTestResultEdit
          test={test as TestResultRp}
          rpCheck2Type={rpCheck2Type}
          onFormInputChange={onFormInputChange}
        />
      )}
      {assemblyType.startsWith('DC') && (
        <DcTestResultEdit
          test={test as TestResultDc}
          onFormInputChange={onFormInputChange}
        />
      )}
      {assemblyType === 'TYPE_2_BYPASS' && (
        <Type2BypassTestResultEdit
          test={test as TestResultType2Bypass}
          onFormInputChange={onFormInputChange}
        />
      )}
      {['PVB', 'SVB'].includes(assemblyType) && (
        <SvbPvbTestResultEdit
          test={test as TestResultSvbPvb}
          onFormInputChange={onFormInputChange}
        />
      )}
      {/*{errors.map((error, index) => (*/}
      {/*  <Text variant="validation" key={index}>*/}
      {/*    {error}*/}
      {/*  </Text>*/}
      {/*))}*/}
    </Accordion>
  );
};

export default BackflowTestResultEdit;
